import { ContentSwitcher } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import type { MultiValue } from 'chakra-react-select';
import { useEffect, useMemo, useState } from 'react';
import type { ComboboxSize, DefaultOptionType } from './combobox';
import Combobox from './combobox';
import ComboboxForPasting from './combobox-for-pasting';

type Dto = {
  id: number;
  name: string | null;
};

type CombinedComboboxProps<T extends Dto> = {
  label: string;
  selectedItems: DefaultOptionType[];
  setHasError: (hasError: boolean) => void;
  onSelect: (items: MultiValue<DefaultOptionType>) => void;
  useFilteredItems: (searchValue: string) => {
    data: T[] | undefined;
    isFetching: boolean;
  };
  useItemsPasting: (pasteValue: number[]) => {
    data: Record<string, T | null> | undefined;
    isFetching: boolean;
  };
  isDisabled?: boolean;
  size?: ComboboxSize;
};

const CombinedCombobox = <T extends Dto>({
  label,
  selectedItems,
  setHasError,
  onSelect,
  useFilteredItems,
  useItemsPasting,
  isDisabled = false,
  size = 'medium',
}: CombinedComboboxProps<T>) => {
  const [showError, setShowError] = useState(false);
  const [selectedMode, setSelectedMode] = useState<string>('paste');
  const [searchValue, setSearchValue] = useState('');
  const { data: dataForSearch, isFetching: isFetchingSearch } =
    useFilteredItems(searchValue);

  const [pasteValue, setPasteValue] = useState<number[] | null>(null);
  const { data: dataForPasting, isFetching: isFetchingPaste } = useItemsPasting(
    pasteValue ?? [],
  );
  const { validItems, noResultKeys } = Object.entries(
    dataForPasting ?? {},
  ).reduce<{
    validItems: T[];
    noResultKeys: string[];
  }>(
    (acc, [key, value]) => {
      if (value !== null) {
        acc.validItems.push(value);
      } else {
        acc.noResultKeys.push(key);
      }
      return acc;
    },
    { validItems: [], noResultKeys: [] },
  );

  const validOptions: DefaultOptionType[] = useMemo(
    () =>
      validItems.map((item) => ({
        id: item.id.toString(),
        label: `${item.id} - ${item.name !== '' && item.name !== null ? item.name : 'N/A'}`,
        hasError: false,
      })),
    [validItems],
  );

  useEffect(() => {
    setHasError(showError);
  }, [showError, setHasError]);

  return (
    <div
      style={isDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <Flex justifyContent={'space-between'} alignItems={'flex-end'} mb={1}>
        <Text fontWeight={500}>{label}</Text>
        <ContentSwitcher
          aria-label="Choose mode"
          width="hug"
          onSwitch={(value) => setSelectedMode(value)}
          size="small"
          disabled={showError || isDisabled}
        >
          <ContentSwitcher.Segment
            aria-controls="paste"
            text="Paste"
            value="paste"
          />
          <ContentSwitcher.Segment
            aria-controls="search"
            text="Search"
            value="search"
          />
        </ContentSwitcher>
      </Flex>
      {selectedMode === 'paste' ? (
        <ComboboxForPasting
          validItems={validOptions}
          noResultKeys={noResultKeys}
          selectedItems={selectedItems}
          hasError={showError}
          finalValue={pasteValue}
          isFetching={isFetchingPaste}
          setFinalValue={setPasteValue}
          onSelect={onSelect}
          setHasError={setShowError}
          size={size}
        />
      ) : (
        <Combobox
          options={
            dataForSearch?.map((item) => ({
              id: item.id.toString(),
              label: `${item.id} - ${item.name !== '' ? item.name : 'N/A'}`,
            })) || []
          }
          selectedItems={selectedItems}
          isLoading={isFetchingSearch}
          onSelect={(selectedItems) => onSelect(selectedItems)}
          inputValue={searchValue}
          onInputChange={(value: string) => setSearchValue(value)}
          placeholder="Start typing to search"
          hasError={showError}
          size={size}
        />
      )}
    </div>
  );
};
export default CombinedCombobox;
